// Clerk instance is a RUNTIME setting (window.__APP_CONFIG__ comes from /config.js, generated at
// container start from CLERK_PUBLISHABLE_KEY) so one image can serve deployments with different
// Clerk apps. The literal below is the dev-instance fallback for local dev / QA and deployments
// that don't set the env var.
declare global {
  interface Window {
    __APP_CONFIG__?: { clerkPublishableKey?: string; clarityProjectId?: string };
  }
}

export const CLERK_PUBLISHABLE_KEY: string =
  window.__APP_CONFIG__?.clerkPublishableKey ||
  'pk_test_Y29tcG9zZWQtbGFyay05Ni5jbGVyay5hY2NvdW50cy5kZXYk';

/** How long the sign-in / sign-up page waits for Clerk's form before offering a way forward. */
export const FORM_TIMEOUT_MS = 10_000;

/** Error text of a script chunk that failed to load (webpack, Vite and browser wordings). */
export const CHUNK_ERROR = /ChunkLoadError|Loading (CSS )?chunk|dynamically imported module|Importing a module script failed/i;

/** Clerk's hosted Account Portal page for this flow, derived from the publishable key's Frontend
 *  API host (clerk.<domain> → accounts.<domain>; <x>.clerk.accounts.dev → <x>.accounts.dev). */
export function hostedClerkUrl(publishableKey: string | undefined, mode: 'sign-in' | 'sign-up',
                               returnTo: string = `${window.location.origin}/`): string | null {
  const frontendApi = clerkFrontendApiHost(publishableKey);
  if (!frontendApi) return null;
  try {
    const accounts = frontendApi.endsWith('.clerk.accounts.dev')
      ? frontendApi.replace(/\.clerk\.accounts\.dev$/, '.accounts.dev')
      : frontendApi.replace(/^clerk\./, 'accounts.');
    if (accounts === frontendApi) return null;
    return `https://${accounts}/${mode}?redirect_url=${encodeURIComponent(returnTo)}`;
  } catch {
    return null;
  }
}

/** The Clerk Frontend API host a publishable key points at (clerk.<domain>, or <x>.clerk.accounts.dev). */
export function clerkFrontendApiHost(publishableKey: string | undefined): string | null {
  if (!publishableKey) return null;
  try {
    const encoded = publishableKey.replace(/^pk_(test|live)_/, '');
    return atob(encoded + '='.repeat((4 - (encoded.length % 4)) % 4)).replace(/\$$/, '') || null;
  } catch {
    return null;
  }
}

/**
 * Open the connection to Clerk's host while the app bundle is still downloading, so Clerk's script
 * and UI chunks don't also pay DNS + TLS setup (hundreds of ms on a slow mobile link — the
 * connections where sign-up chunks were timing out). Both CORS modes: chunk tags vary.
 */
export function preconnectToClerk(publishableKey: string | undefined): void {
  const host = clerkFrontendApiHost(publishableKey);
  if (!host) return;
  for (const crossOrigin of [null, 'anonymous']) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = `https://${host}`;
    if (crossOrigin) link.crossOrigin = crossOrigin;
    document.head.appendChild(link);
  }
}

/** Per-tab flag: a chunk failure already triggered the one automatic reload. */
export const CLERK_RETRY_FLAG = 'clerk-form-auto-retried';
